import {
  ConfirmCandidateSource,
  type IConfirmCandidateSourceEntry,
  type TConfirmCandidateSource,
} from './types';

/**
 * Where the recorded entry is kept between the entry point and the confirm click.
 *
 * Not router state or a prop: the confirm click is several navigations away from
 * the entry point, and not all of those hops can carry state — an email CTA lands
 * on `/dashboard/job/:jobId`, which `history.replace`s to the confirm route, and
 * the Schedule calendar goes through the same redirect. Threading a prop through
 * both would mean touching every screen in between and would still break the
 * moment another redirect is added.
 *
 * `sessionStorage` rather than a module variable because the office can reload the
 * confirm page and still legitimately confirm from it: arriving from an outreach
 * email and hitting refresh, or — on a two-candidate shift — declining the first,
 * refreshing, then confirming the second. A module variable is wiped by the reload
 * and those confirmations would report no source at all.
 */
const STORAGE_KEY = 'gotu.confirmCandidateSource';

const KNOWN_SOURCES: readonly string[] = Object.values(ConfirmCandidateSource);

/**
 * In-memory mirror, and the only store when `sessionStorage` is unavailable
 * (Safari private mode, storage disabled, non-browser test environments). Always
 * belongs to the current document, so it needs no staleness check.
 */
let currentEntry: IConfirmCandidateSourceEntry | undefined;

/** A stored entry, tagged with the document load that recorded it. */
interface IStoredEntry extends IConfirmCandidateSourceEntry {
  /**
   * `performance.timeOrigin` of the document that recorded this entry. Unique per
   * document load, so it distinguishes "this page reloaded" from "the office
   * navigated here fresh" — see {@link readConfirmCandidateSource}.
   */
  documentId?: number;
}

function getSessionStorage(): Storage | undefined {
  try {
    return window.sessionStorage;
  } catch {
    // Accessing `sessionStorage` itself throws when storage is blocked.
    return undefined;
  }
}

function getDocumentId(): number | undefined {
  try {
    return window.performance?.timeOrigin;
  } catch {
    return undefined;
  }
}

/** `'reload'`, `'navigate'`, `'back_forward'`, or undefined when unavailable. */
function getNavigationType(): string | undefined {
  try {
    const [navigation] = window.performance?.getEntriesByType?.('navigation') ?? [];

    return (navigation as PerformanceNavigationTiming | undefined)?.type;
  } catch {
    return undefined;
  }
}

function readStoredEntry(): IStoredEntry | undefined {
  const raw = (() => {
    try {
      return getSessionStorage()?.getItem(STORAGE_KEY);
    } catch {
      return undefined;
    }
  })();

  if (!raw) {
    return undefined;
  }

  try {
    const parsed: unknown = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null) {
      return undefined;
    }

    const { source, shiftId, documentId } = parsed as Record<string, unknown>;

    // Validate against the vocabulary rather than trusting whatever is in
    // storage — a stale key from an older build (or a hand-edited one) must never
    // reach PostHog as a source value we don't recognize.
    if (typeof source !== 'string' || !KNOWN_SOURCES.includes(source)) {
      return undefined;
    }

    return {
      source: source as TConfirmCandidateSource,
      shiftId: typeof shiftId === 'string' ? shiftId : undefined,
      documentId: typeof documentId === 'number' ? documentId : undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * Records how the office is entering the confirm flow. Call this at the entry
 * point (list click, email landing), not at the confirm click.
 *
 * Last write wins: an office who backs out to the Schedule and clicks a different
 * shift should report `schedule`, not the email they arrived from an hour ago.
 */
export function setConfirmCandidateSource(entry: IConfirmCandidateSourceEntry): void {
  currentEntry = entry;

  try {
    const stored: IStoredEntry = { ...entry, documentId: getDocumentId() };
    getSessionStorage()?.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Storage full or blocked — the in-memory mirror still covers this page life.
  }
}

/**
 * Reads the source to report on a confirm event for `shiftId`.
 *
 * Two ways this deliberately reports nothing rather than something wrong:
 *
 * 1. **A different shift.** An office who arrives from an outreach email, backs
 *    out, then confirms an unrelated shift from a screen we don't track would
 *    otherwise have that second confirmation attributed to the email.
 *
 * 2. **A different document, unless this one is a reload.** An entry recorded
 *    before the current page load belongs to a navigation that has ended. Opening
 *    a confirmation-outreach link, or pasting a confirm URL, is a *fresh* arrival
 *    and must not inherit whatever the office happened to click earlier in the tab
 *    — that reported `schedule` for a link that came from an email. A reload is
 *    the exception: the entry still describes how the office got into this flow,
 *    which is the whole reason the entry outlives the page.
 */
export function readConfirmCandidateSource(
  shiftId?: string,
): TConfirmCandidateSource | undefined {
  const stored = readStoredEntry();
  const isFromAnotherDocument =
    stored?.documentId !== undefined &&
    getDocumentId() !== undefined &&
    stored.documentId !== getDocumentId();
  const isInheritable = !isFromAnotherDocument || getNavigationType() === 'reload';

  // The mirror is same-document by construction, so it covers the case where
  // storage is blocked and `stored` is always undefined.
  const entry = (stored && isInheritable ? stored : undefined) ?? currentEntry;

  if (!entry) {
    return undefined;
  }

  const isForAnotherShift =
    Boolean(entry.shiftId) && Boolean(shiftId) && entry.shiftId !== shiftId;

  return isForAnotherShift ? undefined : entry.source;
}

/** Drops the recorded entry. Exported for tests and for logout teardown. */
export function clearConfirmCandidateSource(): void {
  currentEntry = undefined;

  try {
    getSessionStorage()?.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — the mirror is already cleared.
  }
}
